import { lazy, type ComponentType } from "react";
import { reportChunkError } from "@/lib/chunk-error-log";

/**
 * `lazy` résistant aux déploiements : après une mise en ligne, les anciens
 * chunks hachés disparaissent du CDN et l'import dynamique échoue (écran
 * blanc). On réessaie une fois, puis on recharge la page pour récupérer le
 * nouveau manifeste — une seule fois par session pour éviter toute boucle.
 * Chaque échec est journalisé (console + événement `sunset:chunk-error`).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      void reportChunkError(error, { source: "dynamic-import", attempt: 1 });
      await new Promise((r) => setTimeout(r, 400));
      try {
        return await factory();
      } catch (retryError) {
        void reportChunkError(retryError, { source: "dynamic-import", attempt: 2 });
        if (typeof window !== "undefined") {
          const key = "sunset:chunk-reloaded";
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            window.location.reload();
            return await new Promise<{ default: T }>(() => {});
          }
        }
        throw error;
      }
    }
  });
}

